'use client'

import LightweightCarousel from '@/components/common/LightweightCarousel'
import ResponsiveSection from '@/components/common/ResponsiveSection'
import { H2 } from '@/components/common/Typo'
import { useModal } from '@/contexts/ModalContext'
import useResponsive from '@/hooks/useResponsive'
import { MODAL_TYPES } from '@/libs/config/modal'
import { selectSections } from '@/redux/customisation'
import { selectDetailMenuSite } from '@/redux/meta'
import { useGetAllPromotionsFilmstripQuery } from '@/services/local'
import { useTranslate } from '@tolgee/react'
import { cn } from 'oddle-shop-monorepo.design-system'
import { ReactNode } from 'react'
import { useSelector } from 'react-redux'
import PromotionCard from './PromotionCard'

interface PromotionFilmstripSection {
  id: string
  name: string
  settings: {
    showDefaultImage: boolean
    textAlignment: string
    headingsText: string
  }
  type: string
}

export interface PromotionFilmStripProps {
  id: string
}

const PromotionFilmStrip = ({ id }: PromotionFilmStripProps): ReactNode => {
  const { settings } = useSelector(selectSections)[
    id
  ] as PromotionFilmstripSection
  const { t } = useTranslate()
  const { openModal } = useModal()
  const meta = useSelector(selectDetailMenuSite)
  const responsive = useResponsive()
  const promotionFilmstrip = useGetAllPromotionsFilmstripQuery(
    {
      storeOrderId: meta?.storeOrderId,
      mode: meta?.mode,
    },
    {
      skip: !meta?.storeOrderId,
    }
  )

  const getSlidesToShow = (promotions?: Promotion[]) => {
    if (!promotions) {
      return 0
    }
    if (responsive.tablet || promotions.length < 3) {
      return 2
    }
    return 3
  }

  const getPromoCards = ({
    promotions,
    showDefaultImage,
  }: {
    promotions?: Promotion[] | null
    showDefaultImage: boolean
  }) => {
    const cards = []

    if (promotions) {
      const promos = (Array.isArray(promotions) ? promotions : []).map(
        (p, i) => (
          <div
            key={i}
            role="button"
            onClick={() =>
              openModal(MODAL_TYPES.PROMOTION_DETAILS, {
                promotion: p,
                showDefaultImage,
              })
            }
            className={cn(
              'inline-block w-full max-w-[350px] p-0.5 align-bottom md:p-1',
              'focus:outline-none'
            )}
          >
            <PromotionCard promotion={p} showDefaultImage={showDefaultImage} />
          </div>
        )
      )
      cards.push(...promos)
    }

    return cards
  }

  const slidesToShow = getSlidesToShow(
    promotionFilmstrip?.data?.data?.payload?.promotions
  )
  const showArrows =
    !responsive.mobile &&
    (promotionFilmstrip?.data?.data?.payload?.promotions || []).length >
      slidesToShow

  const promotionCards = getPromoCards({
    promotions: promotionFilmstrip?.data?.data?.payload?.promotions,
    showDefaultImage: settings.showDefaultImage,
  })

  return promotionCards.length > 0 ? (
    <ResponsiveSection>
      <ResponsiveSection.Container
        className={cn(
          'text-center',
          settings.textAlignment === 'left' && 'text-left',
          settings.textAlignment === 'right' && 'text-right',
          settings.textAlignment === 'center' && 'text-center'
        )}
      >
        <H2 sectionHeading>{t(settings.headingsText)}</H2>
      </ResponsiveSection.Container>
      <ResponsiveSection.Container className={cn('max-w-[100vw] py-0.5')}>
        {responsive.mobile || promotionCards.length < 4 ? (
          <div className="overflow-x-auto overflow-y-visible whitespace-nowrap pb-4 text-center">
            {promotionCards}
          </div>
        ) : (
          <LightweightCarousel
            slidesToShow={slidesToShow}
            spaceBetween={16}
            showArrows={showArrows}
            className="w-full"
            options={{
              dragFree: responsive.mobile,
              containScroll: 'trimSnaps',
            }}
          >
            {promotionCards}
          </LightweightCarousel>
        )}
      </ResponsiveSection.Container>
    </ResponsiveSection>
  ) : null
}

export default PromotionFilmStrip
